import React, { useState, useEffect } from 'react'
import { useAuth } from '../hooks/useSupabaseAuth'
import { SubscriptionService } from '../services/subscriptionService'
import { Check, X, Clock, CreditCard, User, Calendar, DollarSign, Search, Edit2, Save, XCircle } from 'lucide-react'
import { componentClasses, cn } from '../utils/designSystem'

// Custom styles for mobile responsiveness
const customStyles = `
  .mobile-table-container {
    -webkit-overflow-scrolling: touch;
  }
  
  .mobile-table-container::-webkit-scrollbar {
    height: 8px;
  }
  
  .mobile-table-container::-webkit-scrollbar-track {
    background: #f1f5f9;
    border-radius: 4px;
  }
  
  .mobile-table-container::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 4px;
  }
  
  .mobile-table-container::-webkit-scrollbar-thumb:hover {
    background: #94a3b8;
  }
  
  @media (max-width: 768px) {
    .mobile-pagination {
      flex-wrap: wrap;
    }
  }
`

const AdminDashboard = () => {
  const { user } = useAuth()
  const [transactions, setTransactions] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [filter, setFilter] = useState('pending') // 'all', 'pending', 'active', 'expired'
  const [activating, setActivating] = useState(new Set())
  const [currentPage, setCurrentPage] = useState(1)
  const [totalTransactions, setTotalTransactions] = useState(0)
  const [searchTerm, setSearchTerm] = useState('')
  const [filteredTransactions, setFilteredTransactions] = useState([])
  const [editingCredits, setEditingCredits] = useState(new Set())
  const [editValues, setEditValues] = useState({})
  const [updatingCredits, setUpdatingCredits] = useState(new Set())
  const transactionsPerPage = 100

  // Enhanced admin check with proper role-based access
  const isAdmin = user?.user_metadata?.role === 'admin' || 
                  user?.app_metadata?.role === 'admin' ||
                  ['galih@ratu.ai', 'rzjagadita@gmail.com'].includes(user?.email)

  useEffect(() => {
    if (user && isAdmin) {
      setCurrentPage(1) // Reset to first page when filter changes
      loadTransactions()
    }
  }, [user, isAdmin, filter])

  useEffect(() => {
    if (user && isAdmin) {
      loadTransactions()
    }
  }, [currentPage])

  useEffect(() => {
    if (user && isAdmin) {
      setCurrentPage(1) // Reset to first page when search changes
      loadTransactions()
    }
  }, [searchTerm])

  // Early return for unauthorized access - AFTER all hooks are called
  if (!user) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-white mb-4">Akses Ditolak</h1>
          <p className="text-gray-300">Silakan login terlebih dahulu.</p>
        </div>
      </div>
    )
  }

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-white mb-4">Akses Ditolak</h1>
          <p className="text-gray-300">Anda tidak memiliki izin untuk mengakses halaman admin.</p>
        </div>
      </div>
    )
  }

  const getUserRemainingCredits = async (userId) => {
    try {
      const { data: subscription } = await SubscriptionService.getCurrentSubscription(userId)
      // Only return credits if user has an active paid subscription
      // Don't trigger FREE subscription creation for users without active subscriptions
      if (subscription && subscription.subscription_packages?.name !== 'FREE') {
        return subscription.credits_remaining || 0
      }
      // For users without active paid subscriptions, return null to avoid showing 0
      return null
    } catch (error) {
      console.error('Error fetching user credits:', error)
      return null
    }
  }

  const loadTransactions = async () => {
    try {
      setLoading(true)
      // Get all transactions for admin view
      const result = await SubscriptionService.getAllTransactions(filter)
      console.log('[AdminDashboard] Loaded transactions:', result)
      if (result.success) {
        let allTransactions = result.data
        console.log('[AdminDashboard] Total transactions loaded:', allTransactions.length)
        
        // Get remaining credits for all transactions that have active subscriptions
        allTransactions = await Promise.all(
          allTransactions.map(async (transaction) => {
            // Only fetch credits for transactions with active subscriptions
            if (transaction.user_subscriptions?.status === 'active' || 
                (transaction.subscription_status === 'active') ||
                (transaction.status === 'success' && !transaction.subscription_status)) {
              const remainingCredits = await getUserRemainingCredits(transaction.user_id)
              return {
                ...transaction,
                remaining_credits: remainingCredits
              }
            }
            return {
              ...transaction,
              remaining_credits: null // Explicitly set to null for inactive subscriptions
            }
          })
        )
        
        // Apply search filter
        if (searchTerm.trim()) {
          allTransactions = allTransactions.filter(transaction => 
            transaction.user_email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            transaction.order_id?.toLowerCase().includes(searchTerm.toLowerCase())
          )
        }
        
        setFilteredTransactions(allTransactions)
        setTotalTransactions(allTransactions.length)
        
        // Calculate pagination
        const startIndex = (currentPage - 1) * transactionsPerPage
        const endIndex = startIndex + transactionsPerPage
        const paginatedTransactions = allTransactions.slice(startIndex, endIndex)
        
        setTransactions(paginatedTransactions)
      } else {
        setError(result.error)
      }
    } catch (err) {
      setError('Failed to load transactions')
    } finally {
      setLoading(false)
    }
  }

  const handleActivateSubscription = async (transactionId) => {
    try {
      console.log('[AdminDashboard] Attempting to activate transaction:', transactionId)
      setActivating(prev => new Set([...prev, transactionId]))
      const result = await SubscriptionService.activateSubscription(transactionId)
      
      console.log('[AdminDashboard] Activation result:', result)
      
      if (result.success) {
        // Reload transactions to reflect changes
        await loadTransactions()
      } else {
        console.error('[AdminDashboard] Activation failed:', result.error)
        setError(result.error)
      }
    } catch (err) {
      console.error('[AdminDashboard] Activation error:', err)
      setError('Failed to activate subscription')
    } finally {
      setActivating(prev => {
        const newSet = new Set(prev)
        newSet.delete(transactionId)
        return newSet
      })
    }
  }

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0
    }).format(amount)
  }

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('id-ID', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const getStatusBadge = (transaction) => {
    // Determine actual subscription status
    let status = 'pending'
    
    if (transaction.user_subscriptions?.status) {
      status = transaction.user_subscriptions.status
    } else if (transaction.subscription_status) {
      status = transaction.subscription_status
    } else if (transaction.status === 'success') {
      status = 'active'
    } else if (transaction.status === 'cancelled') {
      status = 'cancelled'
    }
    
    const badges = {
      pending: { color: 'bg-yellow-900 text-yellow-300 border border-yellow-700', icon: Clock, text: 'Pending' },
      active: { color: 'bg-green-900 text-green-300 border border-green-700', icon: Check, text: 'Active' },
      expired: { color: 'bg-red-900 text-red-300 border border-red-700', icon: X, text: 'Expired' },
      cancelled: { color: 'bg-gray-900 text-gray-300 border border-gray-700', icon: X, text: 'Cancelled' }
    }
    
    const badge = badges[status] || badges.pending
    const Icon = badge.icon
    
    return (
      <span className={cn('inline-flex items-center px-2 py-1 rounded-full text-xs font-medium', badge.color)}>
        <Icon className="w-3 h-3 mr-1" />
        {badge.text}
      </span>
    )
  }

  const handleCancelSubscription = async (transactionId) => {
    try {
      setActivating(prev => new Set([...prev, transactionId]))
      const result = await SubscriptionService.cancelSubscription(transactionId)
      
      if (result.success) {
        // Reload transactions to reflect changes
        await loadTransactions()
      } else {
        setError(result.error)
      }
    } catch (err) {
      setError('Failed to cancel subscription')
    } finally {
      setActivating(prev => {
        const newSet = new Set(prev)
        newSet.delete(transactionId)
        return newSet
      })
    }
  }

  const getSubscriptionStatus = (transaction) => {
    if (transaction.user_subscriptions?.status) {
      return transaction.user_subscriptions.status
    } else if (transaction.subscription_status) {
      return transaction.subscription_status
    } else if (transaction.status === 'success') {
      return 'active'
    } else if (transaction.status === 'cancelled') {
      return 'cancelled'
    }
    return 'pending'
  }

  const handleEditCredits = (transactionId, currentCredits) => {
    setEditingCredits(prev => new Set([...prev, transactionId]))
    setEditValues(prev => ({
      ...prev,
      [transactionId]: currentCredits
    }))
  }

  const handleCancelEdit = (transactionId) => {
    setEditingCredits(prev => {
      const newSet = new Set(prev)
      newSet.delete(transactionId)
      return newSet
    })
    setEditValues(prev => {
      const newValues = { ...prev }
      delete newValues[transactionId]
      return newValues
    })
  }

  const handleSaveCredits = async (transactionId, userId) => {
    try {
      const newCredits = parseInt(editValues[transactionId])
      if (isNaN(newCredits) || newCredits < 0) {
        setError('Kredit harus berupa angka positif')
        return
      }

      setUpdatingCredits(prev => new Set([...prev, transactionId]))
      
      const result = await SubscriptionService.updateUserCredits(userId, newCredits)
      
      if (result.success) {
        // Update local state
        setTransactions(prev => prev.map(transaction => 
          transaction.id === transactionId 
            ? { ...transaction, remaining_credits: newCredits }
            : transaction
        ))
        
        // Clear edit state
        handleCancelEdit(transactionId)
        setError(null)
      } else {
        setError(result.error)
      }
    } catch (err) {
      setError('Gagal mengupdate kredit')
    } finally {
      setUpdatingCredits(prev => {
        const newSet = new Set(prev)
        newSet.delete(transactionId)
        return newSet
      })
    }
  }

  const handleCreditInputChange = (transactionId, value) => {
    setEditValues(prev => ({
      ...prev,
      [transactionId]: value
    }))
  }



  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: customStyles }} />
      <div className={cn(componentClasses.main, "min-h-screen p-4 md:p-6")}>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-text-primary mb-2">Admin Dashboard</h1>
          <p className="text-text-secondary">Manage subscription transactions and activations</p>
        </div>

        {/* Filters */}
        <div className="mb-6">
          <div className="flex flex-wrap gap-2">
            {['all', 'pending', 'active', 'expired', 'cancelled'].map((filterOption) => (
              <button
                key={filterOption}
                onClick={() => setFilter(filterOption)}
                className={cn(
                  "px-4 py-2 rounded-lg text-sm font-medium transition-colors",
                  filter === filterOption
                    ? "bg-brand-primary text-white"
                    : "bg-bg-tertiary text-text-secondary hover:bg-bg-quaternary"
                )}
              >
                {filterOption.charAt(0).toUpperCase() + filterOption.slice(1)}
              </button>
            ))}
          </div>
        </div>

        {/* Search Bar */}
        <div className="mb-6">
          <div className="relative max-w-md">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="h-5 w-5 text-text-tertiary" />
            </div>
            <input
              type="text"
              placeholder="Cari berdasarkan email atau order ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="block w-full pl-10 pr-3 py-2 border border-border-primary rounded-lg bg-bg-secondary text-text-primary placeholder-text-tertiary focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-transparent"
            />
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-800 text-sm">{error}</p>
          </div>
        )}

        {/* Pagination Info */}
        {totalTransactions > 0 && (
          <div className="mb-4 text-sm text-text-secondary">
            Showing {((currentPage - 1) * transactionsPerPage) + 1} to {Math.min(currentPage * transactionsPerPage, totalTransactions)} of {totalTransactions} transactions
          </div>
        )}

        {/* Transactions Table */}
        <div className="bg-bg-secondary rounded-xl border border-border-primary overflow-hidden">
          {/* Mobile responsive wrapper */}
          <div className="block md:hidden">
            <div className="p-4 text-sm text-text-secondary border-b border-border-secondary">
              Scroll horizontally to view all columns →
            </div>
          </div>
          {loading ? (
            <div className="p-8 text-center">
              <div className="animate-spin w-8 h-8 border-2 border-brand-primary border-t-transparent rounded-full mx-auto mb-4"></div>
              <p className="text-text-secondary">Loading transactions...</p>
            </div>
          ) : transactions.length === 0 ? (
            <div className="p-8 text-center">
              <CreditCard className="w-12 h-12 text-text-tertiary mx-auto mb-4" />
              <p className="text-text-secondary">No transactions found</p>
            </div>
          ) : (
            <div className="overflow-x-auto mobile-table-container">
              <table className="w-full text-sm">
                <thead className="bg-bg-tertiary sticky top-0 z-10">
                  <tr>
                    <th className="px-2 md:px-3 py-2 md:py-3 text-left text-xs font-medium text-text-tertiary uppercase tracking-wider w-32 md:w-40 lg:w-48">
                      Email
                    </th>
                    <th className="px-2 md:px-3 py-2 md:py-3 text-left text-xs font-medium text-text-tertiary uppercase tracking-wider w-24 md:w-32 lg:w-40">
                      Order ID
                    </th>
                    <th className="px-2 md:px-3 py-2 md:py-3 text-left text-xs font-medium text-text-tertiary uppercase tracking-wider w-20 md:w-24 lg:w-28">
                      Status
                    </th>
                    <th className="px-2 md:px-3 py-2 md:py-3 text-left text-xs font-medium text-text-tertiary uppercase tracking-wider w-24 md:w-28 lg:w-32">
                      Actions
                    </th>
                    <th className="px-2 md:px-3 py-2 md:py-3 text-left text-xs font-medium text-text-tertiary uppercase tracking-wider w-28 md:w-32 lg:w-40">
                      Package
                    </th>
                    <th className="px-2 md:px-3 py-2 md:py-3 text-left text-xs font-medium text-text-tertiary uppercase tracking-wider w-32 md:w-36 lg:w-40">
                      Credits Tersisa
                    </th>
                    <th className="px-2 md:px-3 py-2 md:py-3 text-left text-xs font-medium text-text-tertiary uppercase tracking-wider w-20 md:w-24 lg:w-28">
                      Amount
                    </th>
                    <th className="px-2 md:px-3 py-2 md:py-3 text-left text-xs font-medium text-text-tertiary uppercase tracking-wider w-24 md:w-28 lg:w-32">
                      Created
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border-secondary">
                  {transactions.map((transaction) => (
                    <tr key={transaction.id} className="hover:bg-bg-tertiary">
                      <td className="px-2 md:px-3 py-3 md:py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <User className="w-3 h-3 md:w-4 md:h-4 text-text-tertiary mr-1 md:mr-2 flex-shrink-0" />
                          <span className="text-xs md:text-sm text-text-primary truncate max-w-[120px] md:max-w-[180px]" title={transaction.user_email || 'N/A'}>{transaction.user_email || 'N/A'}</span>
                        </div>
                      </td>
                      <td className="px-2 md:px-3 py-3 md:py-4 whitespace-nowrap text-xs md:text-sm font-mono text-text-primary">
                        <div className="truncate max-w-[100px] md:max-w-[140px]" title={transaction.order_id}>{transaction.order_id}</div>
                      </td>
                      <td className="px-2 md:px-3 py-3 md:py-4 whitespace-nowrap">
                        {getStatusBadge(transaction)}
                      </td>
                      <td className="px-2 md:px-3 py-3 md:py-4 whitespace-nowrap text-xs md:text-sm">
                        {(() => {
                          const subscriptionStatus = getSubscriptionStatus(transaction)
                          
                          if (subscriptionStatus === 'pending') {
                            return (
                              <button
                                onClick={() => handleActivateSubscription(transaction.id)}
                                disabled={activating.has(transaction.id)}
                                className={cn(
                                  "px-2 md:px-3 py-1 rounded-lg text-xs font-medium transition-colors whitespace-nowrap",
                                  activating.has(transaction.id)
                                    ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                                    : "bg-green-100 text-green-800 hover:bg-green-200"
                                )}
                              >
                                {activating.has(transaction.id) ? 'Activating...' : 'Activate'}
                              </button>
                            )
                          }
                          
                          if (subscriptionStatus === 'active') {
                            return (
                              <button
                                onClick={() => handleCancelSubscription(transaction.id)}
                                disabled={activating.has(transaction.id)}
                                className={cn(
                                  "px-2 md:px-3 py-1 rounded-lg text-xs font-medium transition-colors whitespace-nowrap",
                                  activating.has(transaction.id)
                                    ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                                    : "bg-red-100 text-red-800 hover:bg-red-200"
                                )}
                              >
                                {activating.has(transaction.id) ? 'Canceling...' : 'Cancel'}
                              </button>
                            )
                          }
                          
                          if (subscriptionStatus === 'expired') {
                             return (
                               <span className="text-xs text-red-600 whitespace-nowrap">Expired</span>
                             )
                           }
                           
                           if (subscriptionStatus === 'cancelled') {
                             return (
                               <span className="text-xs text-gray-600 whitespace-nowrap">Cancelled</span>
                             )
                           }
                           
                           return null
                        })()}
                      </td>
                      <td className="px-2 md:px-3 py-3 md:py-4 whitespace-nowrap">
                        <div className="text-xs md:text-sm text-text-primary font-medium truncate max-w-[100px] md:max-w-[140px]" title={transaction.package_name}>{transaction.package_name}</div>
                        <div className="text-xs text-text-tertiary">{transaction.credits} credits</div>
                      </td>
                      <td className="px-2 md:px-3 py-3 md:py-4 whitespace-nowrap text-xs md:text-sm text-text-primary">
                        {(() => {
                          const subscriptionStatus = getSubscriptionStatus(transaction)
                          const hasActiveSubscription = subscriptionStatus === 'active'
                          
                          if (!hasActiveSubscription) {
                            return (
                              <div className="flex items-center">
                                <span className="text-text-tertiary">-</span>
                              </div>
                            )
                          }
                          
                          if (editingCredits.has(transaction.id)) {
                            return (
                              <div className="flex items-center space-x-1">
                                <input
                                  type="number"
                                  min="0"
                                  value={editValues[transaction.id] || ''}
                                  onChange={(e) => handleCreditInputChange(transaction.id, e.target.value)}
                                  className="w-16 px-1 py-1 text-xs border border-border-primary rounded bg-bg-secondary text-text-primary focus:outline-none focus:ring-1 focus:ring-brand-primary"
                                  disabled={updatingCredits.has(transaction.id)}
                                />
                                <button
                                  onClick={() => handleSaveCredits(transaction.id, transaction.user_id)}
                                  disabled={updatingCredits.has(transaction.id)}
                                  className="p-1 text-green-600 hover:text-green-800 disabled:opacity-50"
                                  title="Simpan"
                                >
                                  <Save className="w-3 h-3" />
                                </button>
                                <button
                                  onClick={() => handleCancelEdit(transaction.id)}
                                  disabled={updatingCredits.has(transaction.id)}
                                  className="p-1 text-red-600 hover:text-red-800 disabled:opacity-50"
                                  title="Batal"
                                >
                                  <XCircle className="w-3 h-3" />
                                </button>
                              </div>
                            )
                          }
                          
                          return (
                            <div className="flex items-center space-x-2">
                              <CreditCard className="w-3 h-3 md:w-4 md:h-4 text-text-tertiary flex-shrink-0" />
                              <span className="font-medium text-brand-primary">
                                {transaction.remaining_credits !== undefined && transaction.remaining_credits !== null ? transaction.remaining_credits : (transaction.remaining_credits === null ? 'N/A' : 'Loading...')}
                              </span>
                              {transaction.remaining_credits !== null && (
                                <button
                                  onClick={() => handleEditCredits(transaction.id, transaction.remaining_credits)}
                                  className="p-1 text-text-tertiary hover:text-brand-primary transition-colors"
                                  title="Edit kredit"
                                >
                                  <Edit2 className="w-3 h-3" />
                                </button>
                              )}
                            </div>
                          )
                        })()}
                      </td>
                      <td className="px-2 md:px-3 py-3 md:py-4 whitespace-nowrap text-xs md:text-sm text-text-primary">
                        <div className="truncate max-w-[70px] md:max-w-[100px]" title={formatCurrency(transaction.amount)}>{formatCurrency(transaction.amount)}</div>
                      </td>
                      <td className="px-2 md:px-3 py-3 md:py-4 whitespace-nowrap text-xs md:text-sm text-text-secondary">
                        <div className="truncate max-w-[90px] md:max-w-[120px]" title={formatDate(transaction.created_at)}>{formatDate(transaction.created_at)}</div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Pagination */}
         {totalTransactions > transactionsPerPage && (
           <div className="mt-6 flex flex-col md:flex-row items-center justify-between gap-4">
             <div className="text-sm text-text-secondary order-2 md:order-1">
               Page {currentPage} of {Math.ceil(totalTransactions / transactionsPerPage)}
             </div>
             <div className="flex items-center space-x-1 md:space-x-2 order-1 md:order-2 mobile-pagination">
              <button
                 onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                 disabled={currentPage === 1}
                 className={cn(
                   "px-2 md:px-3 py-2 rounded-lg text-xs md:text-sm font-medium transition-colors",
                   currentPage === 1
                     ? "bg-bg-tertiary text-text-tertiary cursor-not-allowed"
                     : "bg-bg-tertiary text-text-secondary hover:bg-bg-quaternary"
                 )}
               >
                 <span className="hidden md:inline">Previous</span>
                 <span className="md:hidden">‹</span>
               </button>
              
              {/* Page numbers */}
               <div className="flex items-center space-x-1 overflow-x-auto max-w-[200px] md:max-w-none">
                {(() => {
                  const totalPages = Math.ceil(totalTransactions / transactionsPerPage)
                  const pages = []
                  const maxVisiblePages = 5
                  
                  let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2))
                  let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1)
                  
                  if (endPage - startPage + 1 < maxVisiblePages) {
                    startPage = Math.max(1, endPage - maxVisiblePages + 1)
                  }
                  
                  for (let i = startPage; i <= endPage; i++) {
                    pages.push(
                      <button
                        key={i}
                        onClick={() => setCurrentPage(i)}
                        className={cn(
                           "px-2 md:px-3 py-2 rounded-lg text-xs md:text-sm font-medium transition-colors flex-shrink-0",
                           i === currentPage
                             ? "bg-brand-primary text-white"
                             : "bg-bg-tertiary text-text-secondary hover:bg-bg-quaternary"
                         )}
                      >
                        {i}
                      </button>
                    )
                  }
                  
                  return pages
                })()}
              </div>
              
              <button
                 onClick={() => setCurrentPage(prev => Math.min(prev + 1, Math.ceil(totalTransactions / transactionsPerPage)))}
                 disabled={currentPage === Math.ceil(totalTransactions / transactionsPerPage)}
                 className={cn(
                   "px-2 md:px-3 py-2 rounded-lg text-xs md:text-sm font-medium transition-colors",
                   currentPage === Math.ceil(totalTransactions / transactionsPerPage)
                     ? "bg-bg-tertiary text-text-tertiary cursor-not-allowed"
                     : "bg-bg-tertiary text-text-secondary hover:bg-bg-quaternary"
                 )}
               >
                 <span className="hidden md:inline">Next</span>
                 <span className="md:hidden">›</span>
               </button>
            </div>
          </div>
        )}
        </div>
      </div>
    </>
  )
}

export default AdminDashboard